"use client"

import {
  applicationStatusBadgeVariant,
  formatApplicationStatus,
  isActiveApplicationStatus,
  Badge,
  Button,
  formatClientDate,
} from '@yeshuman/sdk'
import { memo, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { LogOut } from 'lucide-react'
import { ApplicationListCard } from './application-list-card'
import { WithdrawApplicationDialog } from './withdraw-application-dialog'
import { useWithdrawApplication } from '../../hooks/use-withdraw-application'
import type { MyApplication } from '../../hooks/use-my-applications'

const APPLIED_DATE_FORMAT = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
} as const

type ApplicationListCardRowProps = {
  application: MyApplication
}

/** Applied-tab row. Opens the status canvas via `?application=<id>` (see CandidateJobsPage). */
export const ApplicationListCardRow = memo(function ApplicationListCardRow({
  application,
}: ApplicationListCardRowProps) {
  const [searchParams, setSearchParams] = useSearchParams()
  const canWithdraw = isActiveApplicationStatus(application.status)
  const withdraw = useWithdrawApplication({ applicationId: application.id })

  const handleOpen = useCallback(() => {
    const next = new URLSearchParams(searchParams)
    next.set('application', application.id)
    setSearchParams(next)
  }, [application.id, searchParams, setSearchParams])

  return (
    <>
      <ApplicationListCard
        className="h-full"
        agentHighlight={`application:${application.id}`}
        opportunityTitle={application.opportunity_title}
        organisationName={application.organisation_name}
        statusRow={
          <Badge variant={applicationStatusBadgeVariant(application.status)}>
            {formatApplicationStatus(application.status)}
          </Badge>
        }
        footerMeta={
          <div className="text-sm text-muted-foreground">
            Applied {formatClientDate(application.applied_at, APPLIED_DATE_FORMAT)}
            {application.current_stage ? (
              <span className="mt-1 block sm:mt-0 sm:inline">
                <span aria-hidden className="mx-1.5 hidden sm:inline">
                  ·
                </span>
                Stage: {application.current_stage.stage_name}
              </span>
            ) : null}
          </div>
        }
        footerActions={
          canWithdraw ? (
            <Button
              type="button"
              variant="outlineDestructive"
              size="sm"
              disabled={withdraw.isPending}
              onClick={(event) => {
                event.stopPropagation()
                withdraw.openDialog()
              }}
            >
              <LogOut className="h-4 w-4" aria-hidden />
              Withdraw
            </Button>
          ) : undefined
        }
        onOpen={handleOpen}
        openAriaLabel={`Open application for ${application.opportunity_title}`}
      />
      {canWithdraw ? <WithdrawApplicationDialog {...withdraw.dialogProps} /> : null}
    </>
  )
})
